import Bubble from '__core/modules/atoms/Bubble';
import Icon from '__core/modules/atoms/Icons';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import React, { useEffect, useId, useRef, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';

const HeaderTabs = ({ align = 'default', idPrefix, isURLBased = false, onChangeTab, onDisableTab = () => {}, tabActive, tabs = [], typeView = 'default' }) => {
  const [activeTabState, setActiveTabState] = useState(tabActive);
  const [focusedTabId, setFocusedTabId] = useState(null);
  const [hasChangedPath, setHasChangedPath] = useState(false);
  const [tabToChangeTo, setTabToChangeTo] = useState(tabs[0]);
  const generatedId = useId();
  const prefix = idPrefix || generatedId;
  const tabRefs = useRef({});

  const history = useHistory();

  useEffect(() => {
    return history.listen(() => {
      setHasChangedPath(true);
    });
  }, [history]);

  function onClickChangeTab(tab) {
    if (isURLBased) {
      setTabToChangeTo(tab);
    } else if (tab.disabled) {
      onDisableTab(tab.id);
    } else {
      setActiveTabState(tab.id);
      setFocusedTabId(tab.id);
    }
    if (tab.onClick) {
      tab.onClick();
    }
  }

  function focusTabById(tabId) {
    const el = tabRefs.current[tabId];
    if (el && typeof el.focus === 'function') el.focus();
  }

  function moveFocus(currentId, direction) {
    const enabled = tabs.filter((t) => !t.disabled);
    if (enabled.length === 0) return;
    const currentIdx = enabled.findIndex((t) => t.id === currentId);
    let nextIdx;
    if (direction === 'next') {
      nextIdx = currentIdx === -1 ? 0 : (currentIdx + 1) % enabled.length;
    } else if (direction === 'prev') {
      nextIdx = currentIdx === -1 ? enabled.length - 1 : (currentIdx - 1 + enabled.length) % enabled.length;
    } else if (direction === 'first') {
      nextIdx = 0;
    } else if (direction === 'last') {
      nextIdx = enabled.length - 1;
    } else {
      return;
    }
    const nextId = enabled[nextIdx].id;
    setFocusedTabId(nextId);
    focusTabById(nextId);
  }

  function onKeyDownTab(event, tab) {
    if (event.target !== event.currentTarget) return;
    switch (event.key) {
      case 'Enter':
      case ' ':
        event.preventDefault();
        onClickChangeTab(tab);
        break;
      case 'ArrowRight':
        event.preventDefault();
        moveFocus(tab.id, 'next');
        break;
      case 'ArrowLeft':
        event.preventDefault();
        moveFocus(tab.id, 'prev');
        break;
      case 'Home':
        event.preventDefault();
        moveFocus(tab.id, 'first');
        break;
      case 'End':
        event.preventDefault();
        moveFocus(tab.id, 'last');
        break;
      default:
        break;
    }
  }

  useEffect(() => {
    if (hasChangedPath && isURLBased && tabToChangeTo) {
      setActiveTabState(tabToChangeTo.id);
      setHasChangedPath(false);
    }
  }, [hasChangedPath]);

  useEffect(() => {
    if (onChangeTab) {
      onChangeTab(activeTabState);
    }
  }, [activeTabState]);

  useEffect(() => {
    setActiveTabState(tabActive);
    setFocusedTabId(null);
  }, [tabActive]);

  function renderText(text, icon, active) {
    return (
      <div className={`header-tabs__tab-text-container `}>
        {icon && (
          <div className="header-tabs__tab-icon">
            <Icon type={icon} color={active ? 'primary' : 'default'} />
          </div>
        )}
        <span className="header-tabs__tab-text">{text}</span>
      </div>
    );
  }

  return (
    <div role="tablist" className={clsx('header-tabs', `header-tabs__type-view-${typeView}`, `header-tabs__align-${align}`)}>
      {tabs.map((tab) => {
        const isActive = tab.id === activeTabState;
        const rovingId = focusedTabId ?? activeTabState;
        const isFocusable = !tab.disabled && tab.id === rovingId;
        return (
          <div
            key={tab.id}
            ref={(el) => {
              tabRefs.current[tab.id] = el;
            }}
            id={`${prefix}-tab-${tab.id}`}
            role="tab"
            aria-selected={isActive}
            aria-disabled={tab.disabled || undefined}
            aria-controls={`${prefix}-panel-${tab.id}`}
            tabIndex={isFocusable ? 0 : -1}
            onClick={() => {
              onClickChangeTab(tab);
            }}
            onKeyDown={(event) => onKeyDownTab(event, tab)}
            className={clsx('header-tabs__tab', `header-tabs__tab--id-${tab.id}`, {
              'header-tabs__tab--active': isActive,
              'header-tabs__tab--with-icon': tab.icon,
              'header-tabs__tab--disabled': tab.disabled,
            })}
          >
            {tab.bubble && (
              <div className="header-tabs__bubble">
                <Bubble />
              </div>
            )}
            {tab.to && <Link to={tab.to}>{renderText(tab.name, tab.icon, isActive)}</Link>}
            {!tab.to && renderText(tab.name, tab.icon, isActive)}
          </div>
        );
      })}
    </div>
  );
};

HeaderTabs.propTypes = {
  align: PropTypes.oneOf(['default', 'left', 'right']),
  idPrefix: PropTypes.string,
  isURLBased: PropTypes.bool,
  onChangeTab: PropTypes.func,
  onDisableTab: PropTypes.func,
  tabActive: PropTypes.string,
  tabs: PropTypes.array, // tabs: [{id: 1, name: "primera Tab"}]
  typeView: PropTypes.oneOf(['default', 'default-bold', 'border', 'background', 'background-round', 'vertical']),
};

export default HeaderTabs;
